import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Shield, Search, RefreshCw, Users, ExternalLink, ChevronRight, Maximize2 } from 'lucide-react'
import { Link } from 'react-router-dom'
import { clsx } from 'clsx'
import { api } from '../lib/api'
import ShareViewButton from '../components/ShareViewButton'

interface Actor {
  id?: string
  mitre_id?: string
  misp_uuid?: string
  name: string
  aliases: string[]
  sponsor_country: string
  sponsor_code?: string
  category: 'nation-state' | 'ransomware' | 'criminal' | 'hacktivist' | 'unknown'
  description?: string
  motivation?: string
  first_seen?: string
  last_seen?: string
  software?: string[]
  targets?: string[]
  refs?: string[]
  sources: string[]
}

interface Catalog {
  actors: Actor[]
  total: number
  full_total: number
  by_sponsor: Record<string, number>
  by_category: Record<string, number>
  by_source: Record<string, number>
  generated_at: string | null
  stale: boolean
}

const CAT_COLOR: Record<string, string> = {
  'nation-state': 'text-rose-light border-rose/30 bg-rose/10',
  'ransomware':   'text-amber-light border-amber/30 bg-amber/10',
  'criminal':     'text-purple-light border-purple/30 bg-purple/10',
  'hacktivist':   'text-blue-light border-blue/30 bg-blue/10',
  'unknown':      'text-text-muted border-border bg-surface-hover',
}

export default function ThreatActorsPage() {
  const [data, setData] = useState<Catalog | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [q, setQ] = useState(searchParams.get('q') || '')
  const [country, setCountry] = useState<string>(searchParams.get('country') || 'all')
  const [category, setCategory] = useState<string>(searchParams.get('category') || 'all')

  // Sync filter state → URL so the view is shareable
  useEffect(() => {
    const t = setTimeout(() => {
      const next = new URLSearchParams()
      if (q)                  next.set('q', q)
      if (country  !== 'all') next.set('country', country)
      if (category !== 'all') next.set('category', category)
      setSearchParams(next, { replace: true })
    }, 150)
    return () => clearTimeout(t)
  }, [q, country, category, setSearchParams])
  const [selected, setSelected] = useState<Actor | null>(null)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const r = await api.get('/api/cyber/threat-actors', {
        params: { q: q || undefined, country: country !== 'all' ? country : undefined, category: category !== 'all' ? category : undefined, limit: 500 },
      })
      setData(r.data)
    } finally { setLoading(false) }
  }, [q, country, category])

  useEffect(() => { fetchData() }, [fetchData])

  const forceRefresh = async () => {
    setRefreshing(true)
    try {
      await api.post('/api/cyber/threat-actors/refresh')
      await fetchData()
    } finally { setRefreshing(false) }
  }

  const countries = useMemo(() => {
    if (!data) return []
    return Object.entries(data.by_sponsor)
      .sort(([, a], [, b]) => b - a)
      .map(([name, n]) => ({ name, n }))
  }, [data])

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
            <Users size={24} className="text-rose" /> Threat Actor Catalog
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            Merged from MITRE ATT&CK + MISP Galaxy · {data?.full_total ?? '—'} actors tracked
          </p>
        </div>
        <button type="button" onClick={forceRefresh} disabled={refreshing} className="btn-secondary text-xs">
          <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} /> Rebuild catalog
        </button>
      </div>

      {data?.stale && (
        <div className="card p-3 text-xs text-amber-light border-amber/30 bg-amber/5">
          No cached catalog yet. Click <b>Rebuild catalog</b> to fetch MITRE ATT&CK + MISP Galaxy (takes ~30s).
        </div>
      )}

      {data && data.total > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Stat label="Total actors"    value={data.full_total} tone="blue" />
          <Stat label="Nation-state"    value={data.by_category['nation-state'] || 0} tone="rose" />
          <Stat label="Ransomware"      value={data.by_category['ransomware'] || 0} tone="amber" />
          <Stat label="In both feeds"   value={data.by_source.both || 0} tone="emerald" hint="MITRE ∩ MISP" />
          <Stat label="Countries"       value={Object.keys(data.by_sponsor).length} tone="purple" />
        </div>
      )}

      <div className="card p-3 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted" />
          <input
            data-kbd-search
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="APT29, LockBit, Fancy Bear… (press /)"
            className="input pl-8 py-1.5 text-xs"
          />
        </div>
        <select
          value={country}
          onChange={e => setCountry(e.target.value)}
          aria-label="Filter by sponsor country"
          title="Sponsor country"
          className="input w-auto py-1.5 text-xs"
        >
          <option value="all">All countries</option>
          {countries.map(c => <option key={c.name} value={c.name}>{c.name} ({c.n})</option>)}
        </select>
        <select
          value={category}
          onChange={e => setCategory(e.target.value)}
          aria-label="Filter by category"
          title="Category"
          className="input w-auto py-1.5 text-xs"
        >
          <option value="all">All categories</option>
          {Object.entries(data?.by_category || {}).map(([k, v]) => <option key={k} value={k}>{k} ({v})</option>)}
        </select>
        <ShareViewButton className="ml-auto" />
        <span className="text-[10px] text-text-muted">
          {data?.actors?.length ?? 0} shown · {loading ? 'loading' : 'live'}
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className={clsx('card p-0 overflow-hidden', selected ? 'lg:col-span-2' : 'lg:col-span-3')}>
          <div className="max-h-[640px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase tracking-wider text-text-muted sticky top-0 bg-surface-card z-10">
                <tr className="border-b border-border">
                  <th className="text-left py-2 px-3">Actor</th>
                  <th className="text-left py-2 px-3">Sponsor</th>
                  <th className="text-left py-2 px-3">Category</th>
                  <th className="text-left py-2 px-3">Aliases</th>
                  <th className="text-left py-2 px-3">Sources</th>
                  <th className="text-right py-2 px-3"><span className="sr-only">Open</span></th>
                </tr>
              </thead>
              <tbody>
                {(data?.actors || []).map(a => {
                  const isActive = selected?.id === a.id
                  return (
                    <tr
                      key={a.id || a.name}
                      onClick={() => setSelected(isActive ? null : a)}
                      className={clsx(
                        'border-b border-border/50 cursor-pointer',
                        isActive ? 'bg-surface-hover' : 'hover:bg-surface-hover',
                      )}
                    >
                      <td className="py-1.5 px-3">
                        <div className="flex items-center gap-1.5">
                          <span className="font-medium text-text-primary">{a.name}</span>
                          {a.mitre_id && <span className="text-[9px] font-mono text-text-muted">{a.mitre_id}</span>}
                        </div>
                      </td>
                      <td className="py-1.5 px-3 text-text-secondary">{a.sponsor_country}</td>
                      <td className="py-1.5 px-3">
                        <span className={clsx('inline-block px-1.5 py-0.5 rounded text-[10px] border', CAT_COLOR[a.category])}>
                          {a.category}
                        </span>
                      </td>
                      <td className="py-1.5 px-3 text-text-muted text-[10px] truncate max-w-[260px]" title={a.aliases?.join(', ')}>
                        {(a.aliases || []).slice(1, 5).join(', ')}
                        {a.aliases && a.aliases.length > 5 && <span className="text-text-muted/60"> +{a.aliases.length - 5}</span>}
                      </td>
                      <td className="py-1.5 px-3 text-[10px] text-text-muted">
                        {a.sources?.join(' + ')}
                      </td>
                      <td className="py-1.5 px-3 text-right">
                        <Link to={`/app/actor/${encodeURIComponent(a.id || a.name)}`}
                          onClick={e => e.stopPropagation()}
                          className="text-blue-light hover:text-blue inline-flex items-center"
                          title="Open detail page">
                          <Maximize2 size={11} />
                        </Link>
                      </td>
                    </tr>
                  )
                })}
                {(data?.actors || []).length === 0 && !loading && (
                  <tr><td colSpan={5} className="py-8 text-center text-text-muted">No actors match filters.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {selected && (
          <div className="card p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-bold text-text-primary">{selected.name}</h2>
              <button type="button" onClick={() => setSelected(null)} className="text-text-muted text-[10px] hover:text-text-primary">close</button>
            </div>
            <div className="space-y-2 text-xs">
              <div className="flex items-center gap-2">
                <span className={clsx('px-1.5 py-0.5 rounded text-[10px] border', CAT_COLOR[selected.category])}>{selected.category}</span>
                <span className="text-text-secondary">{selected.sponsor_country}</span>
                {selected.mitre_id && <span className="font-mono text-[10px] text-text-muted">{selected.mitre_id}</span>}
              </div>
              {selected.aliases && selected.aliases.length > 1 && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-text-muted">Aliases</div>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {selected.aliases.slice(1).map(a => (
                      <span key={a} className="text-[10px] px-1.5 py-0.5 rounded bg-surface-hover text-text-secondary">{a}</span>
                    ))}
                  </div>
                </div>
              )}
              {selected.description && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-text-muted">Description</div>
                  <p className="text-text-secondary leading-relaxed mt-1">{selected.description}</p>
                </div>
              )}
              {selected.motivation && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-text-muted">Motivation</div>
                  <div className="text-text-secondary mt-1">{selected.motivation}</div>
                </div>
              )}
              {selected.software && selected.software.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-text-muted">Associated software ({selected.software.length})</div>
                  <div className="flex flex-wrap gap-1 mt-1 max-h-40 overflow-y-auto">
                    {selected.software.slice(0, 50).map(s => (
                      <span key={s} className="text-[10px] px-1.5 py-0.5 rounded bg-blue/10 text-blue-light border border-blue/20">{s}</span>
                    ))}
                  </div>
                </div>
              )}
              {selected.targets && selected.targets.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-text-muted">Primary targets</div>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {selected.targets.map(t => (
                      <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-surface-hover text-text-secondary">{t}</span>
                    ))}
                  </div>
                </div>
              )}
              {selected.refs && selected.refs.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-text-muted">References</div>
                  <div className="space-y-1 mt-1">
                    {selected.refs.slice(0, 6).map((u, i) => (
                      <a key={i} href={u} target="_blank" rel="noopener" className="block text-[10px] text-blue-light hover:underline truncate flex items-center gap-1">
                        <ExternalLink size={9} /> {u}
                      </a>
                    ))}
                  </div>
                </div>
              )}
              {selected.first_seen && (
                <div className="text-[10px] text-text-muted pt-2 border-t border-border">
                  First seen in catalog {selected.first_seen}
                  {selected.last_seen && <> · Last updated {selected.last_seen}</>}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function Stat({ label, value, tone, hint }: { label: string; value: number; tone: 'blue' | 'rose' | 'amber' | 'emerald' | 'purple'; hint?: string }) {
  const t = tone === 'rose' ? 'text-rose-light' : tone === 'amber' ? 'text-amber-light' : tone === 'emerald' ? 'text-emerald-light' : tone === 'purple' ? 'text-purple-light' : 'text-blue-light'
  return (
    <div className="card p-4">
      <div className="text-[10px] uppercase tracking-wider text-text-muted">{label}</div>
      <div className={clsx('text-3xl font-bold mt-1', t)}>{value.toLocaleString()}</div>
      {hint && <div className="text-[9px] text-text-muted mt-0.5">{hint}</div>}
    </div>
  )
}
